import * as React from 'react'
import { useDebouncedCallback } from 'use-debounce'

import { Tabs } from '@/components/primitives/Tabs/Tabs'
import { NavigationMenu as NavigationMenuUI, NavigationMenuContent, NavigationMenuItem, NavigationMenuList, NavigationMenuTrigger } from '@/components/ui/navigation-menu'
import { cn, isLinkEntry, isNavigationEntry } from '@/lib/utils'

import { MenuLink } from './MenuLink'

import type { NavigationType } from '@/types/contentful/headerType'

export function NavigationMenu({
  navigation,
  isSolid,
  setShowOverlay,
  className,
}: {
  navigation?: NavigationType[]
  isSolid: boolean
  setShowOverlay: React.Dispatch<React.SetStateAction<boolean>>
  className: string
}) {
  const [leading, setLeading] = React.useState(false)
  // Controlled open state so we can close the menu after a link is clicked: a
  // client-side route change does not blur/close the Radix menu on its own.
  const [value, setValue] = React.useState('')

  const clearNav = () => {
    setShowOverlay(false)
    setLeading(false)
  }

  const debounce = useDebouncedCallback(() => {
    setShowOverlay(true)
  }, 200)

  // Close the open mega menu when a link inside it is clicked. Checking for an anchor
  // means clicking a tab button (not an <a>) keeps the menu open as expected.
  const handleMenuClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('a')) {
      debounce.cancel()
      setValue('')
      clearNav()
    }
  }

  return (
    <div className={cn(className)}>
      <NavigationMenuUI
        value={value}
        onValueChange={(e) => {
          setValue(e)
          if (!leading) {
            // not debounce for the first time
            setShowOverlay(true)
            setLeading(true)
          }

          if (e) {
            debounce()
          } else {
            debounce.cancel()
            clearNav()
          }
        }}
      >
        <NavigationMenuList>
          {navigation?.map((item) => {
            const { title, layout, links } = item.fields
            if (isLinkEntry(links[0])) {
              return (
                <NavigationMenuItem key={item.sys.id}>
                  <NavigationMenuTrigger isSolid={isSolid}>{title}</NavigationMenuTrigger>
                  <NavigationMenuContent isSolid={isSolid} className="w-full">
                    <ul className={cn('grid w-fit min-w-[260px] grid-cols-1 gap-8 p-8 text-base ', layout === 'Two Columns' && 'w-[624] grid-cols-2')} onClick={handleMenuClick}>
                      {links.map((link) => {
                        if (isLinkEntry(link)) {
                          return <MenuLink key={link.sys.id} link={link} />
                        }
                      })}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              )
            }

            if (isNavigationEntry(links[0])) {
              const tabData = links
                ?.map((link) => {
                  if (isNavigationEntry(link)) {
                    const { title, layout, links: subLinks } = link.fields

                    return {
                      title: title,
                      id: title,
                      content: (
                        <div className={cn('grid grid-cols-1 gap-4', layout === 'Two Columns' && ' grid-cols-2')}>
                          {subLinks.map((subLink) => {
                            if (isLinkEntry(subLink)) {
                              return <MenuLink key={subLink.sys.id} link={subLink} />
                            }
                          })}
                        </div>
                      ),
                    }
                  }
                })
                .filter((item) => item !== undefined) as { title: string; id: string; content: React.ReactNode }[]

              return (
                <NavigationMenuItem key={item.sys.id}>
                  <NavigationMenuTrigger isSolid={isSolid}>{title}</NavigationMenuTrigger>
                  <NavigationMenuContent isSolid={isSolid} className="w-full">
                    <div className="w-[584px] p-8" onClick={handleMenuClick}>
                      <Tabs tabs={tabData} defaultValue={tabData[0]?.id} source="menu" classNames={{ tabsList: 'lg:justify-start' }} />
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              )
            }
          })}
        </NavigationMenuList>
      </NavigationMenuUI>
    </div>
  )
}
